import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';

/**
 * @param {string} id
 * @param {string} name
 * @param {string} value
 * @param {string} description
 * @param {string} placeholder
 */
const StyledNekoInput = Styled(props => {
  const { id, name, value = '', description, placeholder = '', onEnter = null,
  readOnly = false, type = 'text', step = 1, min = 1, max = 999, 
  maxLength = 3, className, style, natural = false, ...rest } = props;

  const [ newValue, setNewValue ] = useState(value);
  const isControlled = !!props.onChange;

  useEffect(() => {
    if (!isControlled) {
      setNewValue(value);
    }
  }, [ value ]);

  const onChange = (e) => {
    if (isControlled) { 
      props.onChange(e.target.value, id);
    }
    else {
      setNewValue(e.target.value, id);
    }
  };

  const onKeyPress = (e) => {
    if (onEnter && event.key === 'Enter') {
      e.preventDefault();
      props.onEnter(e.target.value, id);
    }
  };

  const onBlur = (e) => {
    if (props.onBlur && value !== e.target.value) {
      props.onBlur(e.target.value, id);
    }
  };

  const classNames = `neko-input ${natural ? ' natural' : ''}`;

  return (
    <div className={[className].join(' ')} style={style}>
      {type === 'number'
        ? <input className={[classNames].join(' ')} {...rest} id={id} name={name} value={isControlled ? value : newValue} type={type} 
          step={step} min={min} max={max} maxlength={maxLength} 
          placeholder={placeholder} 
          onChange={onChange} onKeyPress={onKeyPress} onBlur={onBlur} readOnly={readOnly} />
        : <input className={[classNames].join(' ')} {...rest} id={id} name={name} value={isControlled ? value : newValue} type={type} 
          spellcheck="false" 
          placeholder={placeholder} 
          onChange={onChange} onKeyPress={onKeyPress} onBlur={onBlur} readOnly={readOnly} />
      }
      {description && <p className="neko-input-description">{description}</p>}
    </div>
  );
})`
  .neko-input {
    font-family: ${Theme.fontFamily};
    font-size: ${Theme.fontSizeText};
    border: 2px solid ${Theme.blue};
    box-sizing: border-box;
    height: 30px;
    background: rgb(0 124 186 / 10%);
    padding: 0 10px;
    width: 100%;

    &.natural {
      border-color: gray;
      border-width: 1px;
    }

    &::placeholder {
      color: rgba(0, 0, 0, 0.25);
    }

    :focus {
      background-color: white;
    }

    :read-only {
      background-color: ${Theme.gray};
    }
  }

  .neko-input-description {
    font-family: ${Theme.fontFamily};
    font-size: ${Theme.fontSizeSmall};
    line-height: 14px;
    margin-top: 8px;
    margin-bottom: 0;
  }
`;

const NekoInput = (props) => {
  return (<StyledNekoInput {...props} />);
};

NekoInput.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf(['number', 'text']),
  name: PropTypes.string,
  value: PropTypes.string,
  description: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  onBlur: PropTypes.func,
  readOnly: PropTypes.bool,
  step: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  natural: PropTypes.bool
};

NekoInput.defaultProps = {
  id: undefined,
  type: 'text',
  name: undefined,
  value: '',
  description: undefined,
  placeholder: '',
  onChange: undefined,
  onEnter: undefined,
  onBlur: undefined,
  readOnly: false,
  step: 1,
  min: 1,
  max: 999,
  maxLength: 3,
  natural: false
};

export { NekoInput };
